import { LandmarkId, type PoseFrame, type Point } from "@pushup/detection";

/**
 * MediaPipe BlazePose 33-landmark indices that map to our LandmarkId enum.
 * The numeric values are intentionally identical (we defined LandmarkId to
 * match BlazePose), but this mapping makes the contract explicit and testable.
 */
const LANDMARK_INDEX_MAP: ReadonlyArray<{ mpIndex: number; id: LandmarkId }> = [
  { mpIndex: 0, id: LandmarkId.Nose },
  { mpIndex: 11, id: LandmarkId.LeftShoulder },
  { mpIndex: 12, id: LandmarkId.RightShoulder },
  { mpIndex: 13, id: LandmarkId.LeftElbow },
  { mpIndex: 14, id: LandmarkId.RightElbow },
  { mpIndex: 15, id: LandmarkId.LeftWrist },
  { mpIndex: 16, id: LandmarkId.RightWrist },
  { mpIndex: 23, id: LandmarkId.LeftHip },
  { mpIndex: 24, id: LandmarkId.RightHip },
  { mpIndex: 25, id: LandmarkId.LeftKnee },
  { mpIndex: 26, id: LandmarkId.RightKnee },
  { mpIndex: 27, id: LandmarkId.LeftAnkle },
  { mpIndex: 28, id: LandmarkId.RightAnkle },
];

/** Minimum visibility to consider a landmark usable. */
const MIN_VISIBILITY = 0.5;

/**
 * A single normalized landmark as returned by MediaPipe's PoseLandmarker.
 * We only read the fields we need; the SDK may include more.
 */
export interface MediaPipeLandmark {
  x: number;
  y: number;
  z: number;
  visibility?: number;
}

/**
 * Convert MediaPipe's 33-landmark array into a {@link PoseFrame} consumable
 * by {@link PushupEngine.feedFrame}.
 *
 * Landmarks with visibility below {@link MIN_VISIBILITY} are excluded so the
 * engine can treat them as "missing" and degrade gracefully.
 */
export function mediaPipeToPoseFrame(
  landmarks: ReadonlyArray<MediaPipeLandmark>,
  timestamp: number,
): PoseFrame {
  const result: PoseFrame = { timestamp, landmarks: {} };

  for (const { mpIndex, id } of LANDMARK_INDEX_MAP) {
    const lm = landmarks[mpIndex];
    if (!lm) continue;

    const visibility = lm.visibility ?? 0;
    if (visibility < MIN_VISIBILITY) continue;

    const point: Point = { x: lm.x, y: lm.y, visibility };
    result.landmarks[id] = point;
  }

  return result;
}

/** Re-export for tests and skeleton drawing. */
export { LANDMARK_INDEX_MAP, MIN_VISIBILITY };
